import React, { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from '../../../hooks/use-update-effect';

import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import { IHardware } from '../../../ts/interface/hardware.interface';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import { history } from '../../../utils';

import { columnsForHardwareV2 } from '../../../tableColumns/stationColumns';
import useSmallScreen from '../../../hooks/useSmallScreen';
import { IStatePillsOption } from '../../../ts/interface/components/redesing/state-pills.interface';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import { updateHardware } from '../../../api/hardware';
import { IStation } from '../../../ts/interface/station.interface';
import useHardwareV2 from '../../../hooks/hardware/query/V2/useHardwareV2';
import { useSystemRelay } from '../../../hooks/system/query/V2/useSystemRelay';

function HardwareLocationCard({ detailedStation }: { detailedStation: IStation }) {
  // GraphQL-sourced system (getSystem by node id) so the organization view does
  // not depend on the system-scoped /currentsystem REST endpoint.
  const { system } = useSystemRelay(detailedStation?.system_info?.id ?? undefined, {
    enabled: Boolean(detailedStation?.system_info?.id),
  });

  const [exportHardwareData, setExportHardwareData] = useState<boolean>(false);
  const [hardwareAtLocation, setHardwareAtLocation] = useState<IHardware[]>([]);
  const isSmallScreen = useSmallScreen();

  const [sizePerPage, setSizePerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [pageNumber, setPageNumber] = useState(0);
  const [lastId, setLastId] = useState<string | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<ISortConfig>({
    key: 'name',
    direction: 'asc',
  });

  const rowClick = useCallback(
    (type: string, row: IHardware) => {
      if (type === 'rental') {
        history.push(`/rentals/${row.id}`);
      } else if (type === 'hardware') {
        if (system.new_hardware) {
          history.push(`/hardware/${row.id}`);
        } else {
          history.push(`/units/${row.id}`);
        }
      }
    },
    [system]
  );

  const {
    refetch: refetchHardware,
    isLoading: isLoadingHardware,
    isRefetching: isRefetchingHardware,
  } = useHardwareV2({
    location_id: detailedStation.id,
    after: lastId,
    system_id: detailedStation.system_info?.id,
    limit: lastId ? sizePerPage : sizePerPage + 1,
  });

  useUpdateEffect(() => {
    refetchHardware().then((res) => {
      if (res.status === 'success') {
        const { data } = res.data;
        setHardwareAtLocation((prevHardware) =>
          lastId ? [...prevHardware, ...data] : data
        );
      }
    });
  }, [lastId, refetchHardware, detailedStation]);

  const columnsHardware = useMemo(() => {
    return columnsForHardwareV2(
      isSmallScreen,
      (row: IHardware, item: IStatePillsOption) => {
        CustomAsyncToast({
          promise: () =>
            updateHardware(
              {
                id: row.old_id,
                state: item.value,
              },
              {
                hardware_type: system.hardware_type,
              }
            ),
          loadingMessage: 'Updating...',
          successMessage: () => {
            return 'Hardware updated successfully!';
          },
          errorMessage: 'Failed to update hardware',
        }).then(() => {
          refetchHardware();
        });
      }
    ) as IColumn[];
  }, [isSmallScreen, refetchHardware, system]);

  return (
    <>
      <PaginationTable
        exportData={exportHardwareData}
        onExport={() => setExportHardwareData(false)}
        buttons={[
          {
            label: 'Export',
            onClick: () => setExportHardwareData(true),
            startIcon: <FileDownloadIcon />,
          },
        ]}
        isLoading={isLoadingHardware || isRefetchingHardware}
        sortDirection={sortConfig.direction}
        sortBy={sortConfig.key}
        onSortChange={(item) => {
          setSortConfig(item as ISortConfig);
          setLastId(null);
          setPageNumber(0);
          setSizePerPage(DEFAULT_ROWS_PER_PAGE);
          setHardwareAtLocation([]);
        }}
        items={hardwareAtLocation || []}
        noDataText={'No hardware at this location.'}
        onClickReturnRow
        showFilter={false}
        onRowCLick={(row: IHardware) => rowClick('hardware', row)}
        columns={columnsHardware}
        rowsPerPage={sizePerPage}
        page={pageNumber}
        onPageChange={(page, pageN: number) => {
          const pageNumberAux = pageN + 1;
          if (hardwareAtLocation?.length / sizePerPage < pageNumberAux) {
            setLastId(hardwareAtLocation[hardwareAtLocation.length - 1].id);
          }
          setPageNumber(pageN);
        }}
        onRowsPerPageChange={(rowsPerPage) => {
          setSizePerPage(Number(rowsPerPage));
          // setPageNumber(0);
          // setLastId(null);
        }}
        searchText={search || ''}
        searchPlaceholder={'Search Hardware'}
        handleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
      />
    </>
  );
}

export default HardwareLocationCard;
